import styled, { css } from 'styled-components';
import Text from '@components/text';
import Image from '@components/image';
import { generateScales } from '@helpers/generate-media-queries';
import { TextLink } from '@components/text-link.styled';
import { defaultTheme } from '@styles/theme.styled';

export const OverflowContainer = styled.div`
  width: 100%;
  position: relative;

  &::before {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    width: 100vw;
    transform: translateX(-50%);
    height: 50%;
    background-color: ${({ theme }) => theme.backgroundColors.dark};
    z-index: -1;
  }
`;

export const StyledCustomLink = styled.a`
  text-decoration: none;
`;

export const ViewLink = styled.a`
  ${({ theme }) => css`
    color: ${theme.colors.secondary};
    display: flex;
    width: fit-content;
    text-decoration: none;
    font-family: ${theme.fontFamily.text};
    font-size: 1.125rem;
    font-weight: 500;
    height: 2.75rem;
    padding: 0 1.375rem;
    align-items: center;
    justify-content: center;
    border-radius: 1.5rem;
    background-color: transparent;
    border: 1.5px solid ${theme.colors.primary};
    ${generateScales('font-size', '1rem', '1.125rem', theme)}
    transition: all ${theme.transitions.short};

    &:hover {
      background-color: ${theme.colors.primary};
      color: ${theme.colors.secondary};
      transform: scale(1.0625);
    }

    &:active {
      transform: scale(1);
    }
  `}
`;

export const CategoryLink = styled.a<{ surfaceColor?: keyof typeof defaultTheme.colors }>`
  ${({ theme, surfaceColor = 'white' }) => css`
    color: ${theme.colors.accent};
    ${TextLink(theme.colors.accent, theme.colors[surfaceColor])}
  `}
`;

export const StyledImage = styled(Image)`
  transition: transform 0.3s ease-in-out;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transform-origin: 0 0;
  width: 100%;
  height: 100%;
  position: absolute;
  object-fit: cover;
`;

interface ImageLinkProps {
  postsType: string;
}

export const ImageLink = styled.a<ImageLinkProps>`
  display: block;
  text-decoration: none;
  width: 100%;
  padding-bottom: ${({ postsType }) =>
    postsType === 'guides_post' || postsType === 'case_studies_post' ? '100%' : '56.25%'};

  &:hover ${StyledImage} {
    transform: scale(1.125) translate(-50%, -50%);
  }
`;

export const ImageContainer = styled.div`
  ${({ theme }) => css`
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 100%;
    min-height: 22rem;

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      min-height: unset;
    }
  `}
`;

interface FeaturedContainerProps {
  postsType: string;
}

export const FeaturedContainer = styled.div<FeaturedContainerProps>`
  align-items: center;
  width: 100%;
  display: grid;
  grid-template-columns: ${({ postsType }) =>
    postsType === 'guides_post' || postsType === 'case_studies_post' ? '1fr 2fr' : '1fr 1fr'};
  column-gap: 2rem;
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: 0 2rem 0 0;
  align-items: center;
  overflow: hidden;
  position: relative;

  ${({ theme }) => css`
    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      row-gap: 1.5rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0 0rem 0rem 0rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      column-gap: 0rem;
      row-gap: 0rem;
    }
  `}
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
  justify-content: center;
  align-items: flex-start;
`;

export const DescriptionContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 3rem;
  align-items: flex-start;
  padding: 3rem 0;

  ${({ theme }) => css`
    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      max-width: 100%;
      row-gap: 2rem;
      padding: 0 2rem 2rem 2rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      row-gap: 1.5rem;
      padding: 1.5rem 1rem 1.5rem 1rem;
    }
  `}
`;

export const Title = styled(Text)``;

export const Description = styled.p`
  ${({ theme }) => css`
    margin: 0;
    color: ${theme.colors.dark};
    font-family: ${theme.fontFamily.text};
    ${generateScales('font-size', '1rem', '1.125rem', theme)}
    font-weight: ${theme.fontWeights.normal};
    line-height: ${theme.lineHeights.lg};
    line-height: 1.5;
  `}
`;

export const Meta = styled.div`
  ${({ theme }) => css`
    margin: 0;
    color: ${theme.colors.muted};
    font-family: ${theme.fontFamily.text};
    ${generateScales('font-size', theme.fontSize.small.mobile, theme.fontSize.small.desktop, theme)}
    font-weight: ${theme.fontWeights.normal};
    line-height: ${theme.lineHeights.base};
    display: flex;
    flex-direction: row;
    row-gap: 0.375rem;
    column-gap: 0.375rem;
    flex-wrap: wrap;
    transition: all ${theme.transitions.short};
  `}
`;

export const DescriptionCategory = styled.p`
  ${({ theme }) => css`
    font-family: ${theme.fontFamily.text};
    ${generateScales('font-size', theme.fontSize.small.mobile, theme.fontSize.small.desktop, theme)}
    line-height: 1;
    margin-bottom: 0.875rem;

    ${CategoryLink} + ${CategoryLink} {
      position: relative;
      margin-left: 0.625rem;

      ::after {
        position: absolute;
        right: 100%;
        top: 50%;
        transform: translateY(-50%);
        color: ${theme.colors.dark} !important;
        content: ',';
        width: 0.5rem;
        line-height: 1.25;
        text-align: left;
        height: 100%;
        display: block;
        pointer-events: none;
      }
    }
  `}
`;
